import React, { useState } from 'react';
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ScopedLink } from '../ScopedLink';
import { useListStaffComplianceRegisterQuery } from '../../store/api/localApi';
import { VettingStatus } from '../../types';

/**
 * The one place the Team screen mentions absence at all — see task-19-brief
 * CONTROLLER ADDENDUM item 3. This module is deliberately non-nagging (same
 * "neutral throughout" rule as the register and profile pages, which render
 * "Not recorded" with no red, no warning icon, no alarm styling), so this
 * nudge is:
 *   - informational only — a plain count, never a push notification
 *   - dismissible — closing it is a real per-visit action, not decorative
 *   - never blocking — it never gates or interrupts anything else on Team
 *   - the one exception to "never say what's missing" the module allows
 *     itself, and even that stays a neutral nudge, not an alarm
 *
 * Counts `NOT_RECORDED` directly off the register rows this component
 * already needs to fetch (`useListStaffComplianceRegisterQuery` — each row
 * carries `vettingStatus`, see list-register handler), rather than deriving
 * it from anything Team.tsx's own member-list query returns.
 */
const StaffComplianceNudge: React.FC<{ locationId: string }> = ({ locationId }) => {
  const { data } = useListStaffComplianceRegisterQuery(locationId, { skip: !locationId });
  const [dismissed, setDismissed] = useState(false);

  const notRecordedCount = (data?.register ?? []).filter(
    (entry) => entry.vettingStatus === VettingStatus.NOT_RECORDED
  ).length;

  if (dismissed || notRecordedCount === 0) return null;

  return (
    <div
      data-testid="staff-compliance-nudge"
      className="mb-6 flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-gray-700"
    >
      <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
      <p className="flex-1 text-sm">
        {notRecordedCount} staff {notRecordedCount === 1 ? 'has' : 'have'} no vetting recorded.{' '}
        <ScopedLink
          to="/staff-compliance"
          data-testid="staff-compliance-nudge-link"
          className="font-medium text-primary-600 hover:text-primary-800"
        >
          View compliance register
        </ScopedLink>
      </p>
      <button
        type="button"
        data-testid="staff-compliance-nudge-dismiss"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="flex-shrink-0 text-gray-400 hover:text-gray-600"
      >
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
};

export default StaffComplianceNudge;
